import React, { Component } from 'react';
import config from 'servicesCommon/config';
import { Button } from 'components/antd';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { injectStripe, StripeProvider, Elements, ReactStripeElements } from 'react-stripe-elements';
import { CardNumberElement, CardExpiryElement, CardCvcElement } from 'react-stripe-elements';
import classNames from 'classnames';
import { ThemeContext } from 'app-themes';
import { isLoading, isFetchSuccess } from 'utils/utils';

import styles from './styles.module.scss';

interface State {
	ccErrorMessages: {
		[name: string]: string | undefined;
	};
	ccComplited: {
		[name: string]: string | undefined;
	};
	brand?: string;
}

interface InitialProps {
	isHidden?: boolean;
	fetchStatus?: FetchStatus;
	hideCreditCard: () => void;
	saveCreditCard: (
		stripe: ReactStripeElements.StripeProps,
		cardElement: stripe.elements.Element
	) => void;
}

type Props = InitialProps & ReactStripeElements.InjectedStripeProps;

class CreditCardForm extends Component<Props, State> {
	static contextType = ThemeContext;

	state = {
		ccErrorMessages: {},
		ccComplited: {},
		brand: undefined,
	};
	baseStyle = {
		base: {
			fontFamily: 'Montserrat',
			fontSize: '12px',
			lineHeight: '32px',
			fontWeight: 500,
			color: '#373d59',
			backgroundColor: '#fff',
			'::placeholder': {
				color: '#b9becd',
			},
		},
		invalid: {
			color: '#373d59',
		},
	};

	componentDidUpdate(prevProps: Props) {
		if (isFetchSuccess(this.props.fetchStatus) && isLoading(prevProps.fetchStatus)) {
			// close after succesful saving
			this.hideCreditCard();
		}
	}

	handleCreditCard = () => {
		const { saveCreditCard } = this.props;
		const { stripe, elements } = this.props;
		if (elements) {
			const cardNumberElement = elements.getElement('cardNumber');

			if (stripe && cardNumberElement) {
				saveCreditCard(stripe, cardNumberElement);
			}
		}
	};

	hideCreditCard = () => {
		const { elements } = this.props;
		if (elements) {
			const cardNumberElement = elements.getElement('cardNumber');
			const cardExpiryElement = elements.getElement('cardExpiry');
			const cardCvcElement = elements.getElement('cardCvc');

			if (cardNumberElement && cardExpiryElement && cardCvcElement) {
				cardNumberElement.clear();
				cardExpiryElement.clear();
				cardCvcElement.clear();
			}

			if (this.props.hideCreditCard) {
				this.props.hideCreditCard();
			}
		}
	};

	handleChange = (res: stripe.elements.ElementChangeResponse, name: string) => {
		let ccErrorMessages = { ...this.state.ccErrorMessages };
		let ccComplited = { ...this.state.ccComplited };
		if (res.error && res.error.message) {
			ccErrorMessages[name] = res.error.message;
		} else {
			delete ccErrorMessages[name];
		}
		ccComplited[name] = res.complete;
		let brand: string | undefined = this.state.brand;
		if (name === 'number') {
			brand = res.brand === 'unknown' ? undefined : res.brand;
		}

		this.setState({
			ccErrorMessages,
			ccComplited,
			brand,
		});
	};

	getErrorNode(filedName: string) {
		const { ccErrorMessages } = this.state;

		return (
			ccErrorMessages[filedName] && (
				<div className={styles.cardFormError}>{ccErrorMessages[filedName]}</div>
			)
		);
	}

	isDisabled() {
		const { ccComplited } = this.state;
		const vals = Object.values(ccComplited);
		return vals.length < 3 || vals.some(item => !item);
	}

	render() {
		const { isHidden } = this.props;
		const { brand } = this.state;
		const theme = this.context;

		return (
			<div className={styles.cardFormWrapper} hidden={isHidden}>
				<div
					className={classNames(styles.cardForm, {
						[styles[`cardForm-${theme}`]]: theme !== '',
					})}
				>
					<label className={classNames(styles.cardFormItem, styles.cardFormNumber)}>
						<span className={styles.cardFormLabel}>Card number </span>
						<div className={styles.cardFormInput}>
							<CardNumberElement
								style={this.baseStyle}
								onChange={response => this.handleChange(response, 'number')}
							/>
							{brand && (
								<span
									className={classNames(styles.cardFormBrand, {
										[styles[`cardFormBrand${brand}`]]: brand !== '',
									})}
								/>
							)}
						</div>
						{this.getErrorNode('number')}
					</label>
					<label className={classNames(styles.cardFormItem, styles.cardFormExpiry)}>
						<span className={styles.cardFormLabel}>Expiry date </span>
						<CardExpiryElement
							style={this.baseStyle}
							onChange={response => this.handleChange(response, 'expiry')}
						/>
						{this.getErrorNode('expiry')}
					</label>
					<label className={classNames(styles.cardFormItem, styles.cardFormCVC)}>
						<span className={styles.cardFormLabel}>CVC </span>
						<CardCvcElement
							style={this.baseStyle}
							onChange={response => this.handleChange(response, 'cvc')}
						/>
						{this.getErrorNode('cvc')}
					</label>
					<Button
						type="default"
						icon="remove"
						size="large"
						onClick={this.hideCreditCard}
					/>
					<Button
						icon="apply"
						type="primary"
						size="large"
						disabled={this.isDisabled()}
						onClick={this.handleCreditCard.bind(this)}
					/>
				</div>
				<div className="modalBoxDevider"></div>
			</div>
		);
	}
}

const Form = injectStripe(CreditCardForm);

const CreditCardFormWithWtripeProvider = (props: InitialProps) => {
	return (
		<StripeProvider apiKey={config.stripePublicKey}>
			<Elements
				fonts={[
					{
						cssSrc: 'https://fonts.googleapis.com/css?family=Montserrat:500',
					},
					{
						src:
							'https://fonts.gstatic.com/s/worksans/v5/QGYsz_wNahGAdqQ43Rh_cqDpp_k.woff2',
						family: 'Work Sans',
						weight: '500',
					},
				]}
			>
				<Form {...props} />
			</Elements>
		</StripeProvider>
	);
};

export default CreditCardFormWithWtripeProvider;
